import { apiFetch } from './client';

export type MediaLibraryMode = 'gifs' | 'memes' | 'uploads';

export interface MediaGalleryCard {
  id: string;
  name: string;
  kind: string;
  categoryName: string | null;
  categoryId: string | null;
  tags: string[];
  thumbnailUrl: string | null;
  posterUrl: string | null;
  averageRating: number | null;
  ratingCount: number;
  currentUserRating: number | null;
  reactionCount: number;
  isAnimated: boolean;
  contentVersion: number;
  canManage: boolean;
}

export interface MediaReactionCount {
  emoji: string;
  count: number;
}

export interface MediaGalleryPage {
  items: MediaGalleryCard[];
  totalCount: number;
  /** Keyset position for the next page, or null when the sort does not support cursors. */
  nextCursor: string | null;
  hasMore: boolean;
}

export interface MediaAdminGridRow {
  id: string;
  name: string;
  description: string | null;
  kind: string;
  categoryName: string | null;
  categoryId: string | null;
  tags: string;
  thumbnailUrl: string | null;
  posterUrl: string | null;
  originalFileName: string;
  extension: string;
  contentType: string;
  fileSizeBytes: number;
  width: number;
  height: number;
  isAnimated: boolean;
  frameCount: number;
  animationDurationMs: number | null;
  averageRating: number | null;
  ratingCount: number;
  reactionCount: number;
  viewCount: number;
  copyCount: number;
  downloadCount: number;
  copyLinkCount: number;
  totalUseCount: number;
  lastUsedUtc: string | null;
  createdUtc: string;
  updatedUtc: string;
  sourceUrl: string | null;
  uploadedByUserId: string;
  contentVersion: number;
  sha256: string;
  canManage: boolean;
}

export interface MediaAdminGridPage {
  items: MediaAdminGridRow[];
  totalCount: number;
}

export interface MediaCategoryChip {
  id: string;
  name: string;
  sortOrder: number;
}

export interface MediaTagFilter {
  id: string;
  name: string;
}

export interface MediaCategoryAdmin {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  usageCount: number;
}

export interface MediaTagAdmin {
  id: string;
  name: string;
  isActive: boolean;
  usageCount: number;
}

function toQuery(params: Record<string, string | number | readonly string[] | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((entry) => qs.append(key, entry));
    } else {
      qs.set(key, String(value));
    }
  }
  const text = qs.toString();
  return text ? `?${text}` : '';
}

/**
 * Stamps the asset's content version onto a derivative URL. The API answers versioned
 * URLs with a long immutable cache, so re-scrolling the gallery costs no requests while
 * an edited asset still busts the cache.
 */
export function versionedMediaUrl(url: string | null, contentVersion: number): string | null {
  if (!url) return null;
  return `${url}${url.includes('?') ? '&' : '?'}v=${contentVersion}`;
}

export async function fetchMediaGallery(opts: {
  library: MediaLibraryMode;
  q?: string;
  categoryId?: string | null;
  tagIds?: string[];
  sort?: string;
  skip?: number;
  take?: number;
  /** Keyset position from a previous page. Preferred over `skip` when the server offers one. */
  cursor?: string;
  signal?: AbortSignal;
}): Promise<MediaGalleryPage> {
  const skip = opts.skip ?? 0;
  const res = await apiFetch(
    `/api/media-library/gallery${toQuery({
      library: opts.library,
      q: opts.q,
      categoryId: opts.categoryId,
      tagIds: opts.tagIds,
      sort: opts.sort ?? 'newest',
      skip,
      take: opts.take ?? 48,
      cursor: opts.cursor
    })}`,
    { signal: opts.signal }
  );
  if (!res.ok) throw new Error('Could not load media gallery.');
  const page = (await res.json()) as Partial<MediaGalleryPage>;
  const items = page.items ?? [];
  // -1 means the server skipped the count for this page; the caller keeps its own total.
  const totalCount = page.totalCount ?? items.length;
  return {
    items,
    totalCount,
    nextCursor: page.nextCursor ?? null,
    hasMore:
      typeof page.hasMore === 'boolean'
        ? page.hasMore
        : totalCount >= 0 && skip + items.length < totalCount
  };
}

export async function fetchMediaAdminGrid(library: MediaLibraryMode): Promise<MediaAdminGridPage> {
  const res = await apiFetch(`/api/media-library/admin/grid${toQuery({ library })}`);
  if (!res.ok) throw new Error('Could not load media admin grid.');
  return (await res.json()) as MediaAdminGridPage;
}

export async function fetchMediaCategories(library: MediaLibraryMode): Promise<MediaCategoryChip[]> {
  if (library === 'uploads') return [];
  const res = await apiFetch(`/api/media-library/categories${toQuery({ library })}`);
  if (!res.ok) throw new Error('Could not load categories.');
  return (await res.json()) as MediaCategoryChip[];
}

export async function fetchMediaTags(library: MediaLibraryMode): Promise<MediaTagFilter[]> {
  const res = await apiFetch(`/api/media-library/tags${toQuery({ library })}`);
  if (!res.ok) throw new Error('Could not load tags.');
  return (await res.json()) as MediaTagFilter[];
}

export async function createMediaTag(name: string): Promise<MediaTagFilter> {
  const res = await apiFetch('/api/media-library/tags', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name })
  });
  if (!res.ok) throw new Error('Could not create tag.');
  return (await res.json()) as MediaTagFilter;
}

export async function fetchAdminCategories(): Promise<MediaCategoryAdmin[]> {
  const res = await apiFetch('/api/media-library/admin/categories');
  if (!res.ok) throw new Error('Could not load categories.');
  return (await res.json()) as MediaCategoryAdmin[];
}

export async function createAdminCategory(body: {
  name: string;
  description?: string;
  sortOrder?: number;
}): Promise<MediaCategoryAdmin> {
  const res = await apiFetch('/api/media-library/admin/categories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error('Could not create category.');
  return (await res.json()) as MediaCategoryAdmin;
}

export async function updateAdminCategory(
  id: string,
  body: { name?: string; description?: string | null; sortOrder?: number; isActive?: boolean }
): Promise<MediaCategoryAdmin> {
  const res = await apiFetch(`/api/media-library/admin/categories/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error('Could not update category.');
  return (await res.json()) as MediaCategoryAdmin;
}

export async function mergeAdminCategory(
  sourceId: string,
  targetId: string
): Promise<MediaCategoryAdmin> {
  const res = await apiFetch(
    `/api/media-library/admin/categories/${encodeURIComponent(sourceId)}/merge`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetId })
    }
  );
  if (!res.ok) throw new Error('Could not merge categories.');
  return (await res.json()) as MediaCategoryAdmin;
}

export async function fetchAdminTags(): Promise<MediaTagAdmin[]> {
  const res = await apiFetch('/api/media-library/admin/tags');
  if (!res.ok) throw new Error('Could not load tags.');
  return (await res.json()) as MediaTagAdmin[];
}

export async function createAdminTag(name: string): Promise<MediaTagAdmin> {
  const res = await apiFetch('/api/media-library/admin/tags', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name })
  });
  if (!res.ok) throw new Error('Could not create tag.');
  return (await res.json()) as MediaTagAdmin;
}

export async function updateAdminTag(
  id: string,
  body: { name?: string; isActive?: boolean }
): Promise<MediaTagAdmin> {
  const res = await apiFetch(`/api/media-library/admin/tags/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error('Could not update tag.');
  return (await res.json()) as MediaTagAdmin;
}

export async function mergeAdminTag(sourceId: string, targetId: string): Promise<MediaTagAdmin> {
  const res = await apiFetch(`/api/media-library/admin/tags/${encodeURIComponent(sourceId)}/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetId })
  });
  if (!res.ok) throw new Error('Could not merge tags.');
  return (await res.json()) as MediaTagAdmin;
}

export interface MediaClearStatsResult {
  clearedStatsRows: number;
  deletedRatings: number;
  deletedReactions: number;
  deletedUsageEvents: number;
}

export interface MediaEmptyLibraryResult {
  library: string;
  deletedAssets: number;
  deletedObjects: number;
}

export interface MediaResetTaxonomyResult {
  deletedCategories: number;
  deletedTags: number;
  restoredCategories: number;
  restoredTags: number;
  reassignedAssets: number;
}

export async function clearMediaLibraryStats(): Promise<MediaClearStatsResult> {
  const res = await apiFetch('/api/media-library/admin/stats/clear', { method: 'POST' });
  if (!res.ok) throw new Error('Could not clear media library stats.');
  return (await res.json()) as MediaClearStatsResult;
}

export async function emptyMediaLibrary(
  library: 'gifs' | 'memes'
): Promise<MediaEmptyLibraryResult> {
  const res = await apiFetch(
    `/api/media-library/admin/libraries/${encodeURIComponent(library)}/empty`,
    { method: 'POST' }
  );
  if (!res.ok) throw new Error(`Could not empty ${library} library.`);
  return (await res.json()) as MediaEmptyLibraryResult;
}

export async function resetMediaLibraryTaxonomy(): Promise<MediaResetTaxonomyResult> {
  const res = await apiFetch('/api/media-library/admin/taxonomy/reset', { method: 'POST' });
  if (!res.ok) throw new Error('Could not reset media library taxonomy.');
  return (await res.json()) as MediaResetTaxonomyResult;
}

export interface MediaIngestItem {
  id: string;
  originalFileName: string;
  state: string;
  assetId: string | null;
  duplicateOfAssetId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface MediaIngestBatch {
  id: string;
  targetKind: string;
  state: string;
  totalCount: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  items: MediaIngestItem[];
}

export async function createIngestBatch(body: {
  library: MediaLibraryMode;
  categoryId?: string | null;
  tagIds?: string[];
}): Promise<MediaIngestBatch> {
  const res = await apiFetch('/api/media-library/admin/ingest/batches', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      library: body.library,
      categoryId: body.categoryId ?? null,
      tagIds: body.tagIds ?? []
    })
  });
  if (!res.ok) throw new Error('Could not start ingest batch.');
  return (await res.json()) as MediaIngestBatch;
}

export async function uploadIngestFiles(
  batchId: string,
  files: File[]
): Promise<MediaIngestBatch> {
  const form = new FormData();
  for (const file of files) form.append('files', file);
  const res = await apiFetch(
    `/api/media-library/admin/ingest/batches/${encodeURIComponent(batchId)}/files`,
    { method: 'POST', body: form }
  );
  if (!res.ok) throw new Error('Could not upload media files.');
  return (await res.json()) as MediaIngestBatch;
}

export async function fetchIngestBatch(batchId: string): Promise<MediaIngestBatch> {
  const res = await apiFetch(
    `/api/media-library/admin/ingest/batches/${encodeURIComponent(batchId)}`
  );
  if (!res.ok) throw new Error('Could not load ingest batch status.');
  return (await res.json()) as MediaIngestBatch;
}

export interface MediaAssetDetail {
  id: string;
  name: string;
  description: string | null;
  kind: string;
  categoryId: string | null;
  categoryName: string | null;
  tagIds: string[];
  tags: string[];
  sourceUrl: string | null;
  originalFileName: string;
  extension: string;
  contentType: string;
  fileSizeBytes: number;
  width: number;
  height: number;
  isAnimated: boolean;
  frameCount: number;
  animationDurationMs: number | null;
  thumbnailUrl: string | null;
  posterUrl: string | null;
  originalUrl: string;
  averageRating: number | null;
  ratingCount: number;
  currentUserRating: number | null;
  currentUserReaction: string | null;
  reactionCount: number;
  topReactions: MediaReactionCount[];
  viewCount: number;
  copyCount: number;
  downloadCount: number;
  copyLinkCount: number;
  totalUseCount: number;
  lastUsedUtc: string | null;
  createdUtc: string;
  updatedUtc: string;
  canManage: boolean;
  isDeleted: boolean;
  uploadedByUserId: string | null;
  uploadedByDisplayName: string | null;
  processingState: string | null;
  processingError: string | null;
  contentVersion: number | null;
  sha256: string | null;
}

export type MediaUsageEventType = 'openFullView' | 'copyImage' | 'download' | 'copyLink';

export interface MediaDeletedAsset {
  id: string;
  name: string;
  kind: string;
  deletedUtc: string | null;
  deletedByUserId: string | null;
}

export async function fetchMediaAsset(id: string): Promise<MediaAssetDetail> {
  const res = await apiFetch(`/api/media-library/assets/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error('Could not load media asset.');
  return (await res.json()) as MediaAssetDetail;
}

export async function updateMediaAsset(
  id: string,
  body: {
    name: string;
    description?: string;
    categoryId?: string | null;
    tagIds?: string[];
    sourceUrl?: string;
  }
): Promise<MediaAssetDetail> {
  const res = await apiFetch(`/api/media-library/admin/assets/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: body.name,
      description: body.description ?? null,
      categoryId: body.categoryId ?? null,
      tagIds: body.tagIds ?? [],
      sourceUrl: body.sourceUrl ?? null
    })
  });
  if (!res.ok) throw new Error('Could not update media asset.');
  return (await res.json()) as MediaAssetDetail;
}

export async function bulkUpdateMediaAssets(body: {
  assetIds: string[];
  updateCategory: boolean;
  categoryId?: string | null;
  updateTags: boolean;
  tagIds?: string[];
}): Promise<{ updatedCount: number }> {
  const res = await apiFetch('/api/media-library/admin/assets/bulk-metadata', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      assetIds: body.assetIds,
      updateCategory: body.updateCategory,
      categoryId: body.categoryId ?? null,
      updateTags: body.updateTags,
      tagIds: body.tagIds ?? []
    })
  });
  if (!res.ok) throw new Error('Could not update selected media.');
  return (await res.json()) as { updatedCount: number };
}

export async function softDeleteMediaAsset(id: string): Promise<void> {
  const res = await apiFetch(`/api/media-library/admin/assets/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error('Could not delete media asset.');
}

export async function restoreMediaAsset(id: string): Promise<void> {
  const res = await apiFetch(`/api/media-library/admin/assets/${encodeURIComponent(id)}/restore`, {
    method: 'POST'
  });
  if (!res.ok) throw new Error('Could not restore media asset.');
}

export async function fetchDeletedMediaAssets(): Promise<MediaDeletedAsset[]> {
  const res = await apiFetch('/api/media-library/admin/assets/deleted');
  if (!res.ok) throw new Error('Could not load recycle bin.');
  return (await res.json()) as MediaDeletedAsset[];
}

export async function replaceMediaAsset(id: string, file: File): Promise<MediaAssetDetail> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiFetch(`/api/media-library/admin/assets/${encodeURIComponent(id)}/replace`, {
    method: 'POST',
    body: form
  });
  if (!res.ok) throw new Error('Could not replace media file.');
  return (await res.json()) as MediaAssetDetail;
}

export async function setMediaRating(id: string, stars: number): Promise<MediaAssetDetail> {
  const res = await apiFetch(`/api/media-library/assets/${encodeURIComponent(id)}/rating`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stars })
  });
  if (!res.ok) throw new Error('Could not save rating.');
  return (await res.json()) as MediaAssetDetail;
}

export async function setMediaReaction(id: string, emoji: string): Promise<MediaAssetDetail> {
  const res = await apiFetch(`/api/media-library/assets/${encodeURIComponent(id)}/reaction`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emoji })
  });
  if (!res.ok) throw new Error('Could not save reaction.');
  return (await res.json()) as MediaAssetDetail;
}

export async function clearMediaReaction(id: string): Promise<MediaAssetDetail> {
  const res = await apiFetch(`/api/media-library/assets/${encodeURIComponent(id)}/reaction`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error('Could not clear reaction.');
  return (await res.json()) as MediaAssetDetail;
}

export async function recordMediaUsage(id: string, eventType: MediaUsageEventType): Promise<void> {
  await apiFetch(`/api/media-library/assets/${encodeURIComponent(id)}/usage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ eventType })
  });
}

export async function uploadPrivateMedia(
  file: File,
  tagIds: string[] = []
): Promise<MediaAssetDetail> {
  const form = new FormData();
  form.append('file', file);
  tagIds.forEach((tagId) => form.append('tagIds', tagId));
  const res = await apiFetch('/api/media-library/uploads', { method: 'POST', body: form });
  if (!res.ok) throw new Error('Could not upload private media.');
  return (await res.json()) as MediaAssetDetail;
}

export async function updatePrivateMedia(
  id: string,
  body: { name: string; description?: string; sourceUrl?: string; tagIds?: string[] }
): Promise<MediaAssetDetail> {
  const res = await apiFetch(`/api/media-library/uploads/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: body.name,
      description: body.description ?? null,
      sourceUrl: body.sourceUrl ?? null,
      tagIds: body.tagIds ?? []
    })
  });
  if (!res.ok) throw new Error('Could not update private media.');
  return (await res.json()) as MediaAssetDetail;
}

export async function deletePrivateMedia(id: string): Promise<void> {
  const res = await apiFetch(`/api/media-library/uploads/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error('Could not delete private media.');
}
